import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Copy, CalendarClock, TrendingUp, Ban } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const hiddenNames = ["Board Meeting", "Team All-Hands","Sales Demo"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await api.get("/dashboard");
    setData(res.data);
  };
  useEffect(() => { load(); }, []);

  const toggleUnbookable = async (val) => {
    setSaving(true);
    try {
      const s = await api.get("/settings");
      await api.put("/settings", { ...s.data, manual_unbookable: val });
      await load();
      toast.success(val ? "Deep Work Mode on — calendar locked" : "Calendar reopened");
    } catch (e) {
      toast.error("Could not update");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/book/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Booking link copied");
  };

  if (!data) return null;
  const pct = data.daily_cap ? Math.min(100, Math.round((data.today_count / data.daily_cap) * 100)) : 0;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Dashboard</h1>
        <p className="mt-1 text-slate-600">Your calendar, protected by design.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 md:col-span-2" data-testid="dashboard-meeting-count-card">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Today's meetings</p>
              <p className="mt-2 text-4xl font-extrabold">
                {data.today_count}<span className="text-slate-400 text-2xl font-bold"> / {data.daily_cap || "∞"}</span>
              </p>
            </div>
            {data.unbookable ? (
              <span data-testid="dashboard-unbookable-status-badge" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full bg-red-50 text-red-800 border border-red-200">
                <Ban className="w-3.5 h-3.5" /> Unbookable
              </span>
            ) : (
              <span data-testid="dashboard-unbookable-status-badge" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full bg-green-50 text-green-800 border border-green-200">
                <ShieldCheck className="w-3.5 h-3.5" /> Bookable
              </span>
            )}
          </div>
          <div className="mt-6">
            <div className="h-3 w-full rounded-full bg-slate-100 overflow-hidden" data-testid="dashboard-cap-progress-bar">
              <div className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-red-500" : "bg-blue-600"}`} style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-2 text-sm text-slate-500">
              {data.unbookable
                ? "Threshold reached — new bookings are blocked for today."
                : `${Math.max(0, (data.daily_cap || 0) - data.today_count)} slots left before your calendar locks.`}
            </p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2 text-slate-500">
            {data.manual_unbookable ? <ShieldOff className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
            <p className="text-xs font-medium uppercase tracking-wider">Deep Work Mode</p>
          </div>
          <p className="mt-3 text-sm text-slate-600">Manually lock your calendar so nobody can book — regardless of your cap.</p>
          <div className="mt-4 flex items-center gap-3">
            <Switch checked={data.manual_unbookable} disabled={saving} onCheckedChange={toggleUnbookable} data-testid="dashboard-unbookable-toggle" />
            <span className="text-sm font-semibold">{data.manual_unbookable ? "Locked" : "Open"}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Upcoming meetings</h2>
          </div>
          {data.upcoming.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No upcoming meetings. Share a booking link to fill your optimal slots.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {data.upcoming.map((b) => (
                <div key={b.booking_id} className="py-3 flex items-center justify-between" data-testid={`upcoming-${b.booking_id}`}>
                  <div>
                    <p className="font-semibold text-sm">{b.template_name} · {b.invitee_name}</p>
                    <p className="text-xs text-slate-500">{b.invitee_email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">{fmtDate(b.date)}</p>
                    <p className="text-xs text-slate-500">{b.start_time}–{b.end_time}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Share links</h2>
          </div>
          <div className="space-y-2">
            {data.templates.filter((t) => t.active && !hiddenNames.includes(t.name)).map((t) => (
              <button key={t.template_id} onClick={() => copyLink(t.slug)} data-testid={`share-link-${t.slug}`}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-colors text-left">
                <span className="text-sm font-medium truncate">{t.name} · {t.duration_minutes}m</span>
                <Copy className="w-4 h-4 text-blue-600 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
